import {createReducer} from "redux-act";
import {
	clearLeagueCreated,
	clearLeagueForJoin,
	clearLeagueJoined,
	clearLeagueRankings,
	clearLeaguesForJoin,
	clearLeagueUsers,
	closeInvitesSentModal,
	closeLeaveLeagueModal,
	closeRemoveUserModal,
	createLeague,
	createLeagueFailed,
	createLeagueSuccess,
	fetchGotcLeague,
	fetchGotcLeagueSuccess,
	fetchLeagueForJoin,
	fetchLeagueForJoinFailed,
	fetchLeagueForJoinSuccess,
	fetchLeagueRankings,
	fetchLeagueRankingsFailed,
	fetchLeagueRankingsSuccess,
	fetchLeagues,
	fetchLeaguesFailed,
	fetchLeaguesForJoin,
	fetchLeaguesForJoinFailed,
	fetchLeaguesForJoinSuccess,
	fetchLeaguesSuccess,
	fetchLeagueUsers,
	fetchLeagueUsersFailed,
	fetchLeagueUsersSuccess,
	ILeaveLeagueModalPayload,
	inviteToLeague,
	inviteToLeagueFailed,
	inviteToLeagueSuccess,
	IRemoveUserModalPayload,
	joinLeague,
	joinLeagueFailed,
	joinLeagueSuccess,
	leaveLeague,
	leaveLeagueFailed,
	leaveLeagueSuccess,
	openInvitesSentModal,
	openLeaveLeagueModal,
	openRemoveUserModal,
	removeUser,
	removeUserFailed,
	removeUserSuccess,
	setContestsForLeagues,
	setLeagueCreated,
	setLeagueJoined,
	setRankingFilters,
	updateLeague,
	updateLeagueFailed,
	updateLeagueSuccess,
} from "modules/actions";
import {RequestState} from "modules/enums";
import {concat, filter, map} from "lodash";
import {ILeague, ILeaguesContests, ILeagueUser, IRanking, IRankingFilters} from "modules/types";

export interface ILeaguesResponse {
	leagues: ILeague[];
	nextPage: boolean;
}

export interface ILeagueRankingsResponse {
	rankings: IRanking[];
	user: IRanking | null;
	nextPage: boolean;
}

export interface ILeagueUsersResponse {
	users: ILeagueUser[];
	nextPage: boolean;
}

interface ILeaguesState {
	leagues: ILeague[];
	gotcLeague: ILeague | null;
	leagueCreated: boolean;
	leaguesForJoin: ILeaguesResponse;
	leagueForJoin?: ILeague;
	leagueRankings: ILeagueRankingsResponse;
	leagueRankingFilters: IRankingFilters;
	leagueUsers: ILeagueUsersResponse;
	leagueJoined: number | null;
	isInvitesSentModalOpen: boolean;
	removeUserModal: IRemoveUserModalPayload | null;
	leaveLeagueModal: ILeaveLeagueModalPayload | null;
	contestsForLeagues: ILeaguesContests[];
	requestState: {
		leagues: RequestState;
		gotcLeagues: RequestState;
		leaguesForJoin: RequestState;
		leagueRankings: RequestState;
		leagueUsers: RequestState;
		default: RequestState;
	};
	error: string | null;
}

const initial: ILeaguesState = {
	leagues: [],
	gotcLeague: null,
	leagueCreated: false,
	leaguesForJoin: {
		leagues: [],
		nextPage: false,
	},
	leagueRankings: {
		rankings: [],
		user: null,
		nextPage: false,
	},
	leagueRankingFilters: {
		country: 0,
		startId: 0,
		stage: "overall",
	},
	leagueUsers: {
		users: [],
		nextPage: false,
	},
	leagueJoined: null,
	isInvitesSentModalOpen: false,
	removeUserModal: null,
	leaveLeagueModal: null,
	contestsForLeagues: [],
	requestState: {
		leagues: RequestState.IDLE,
		gotcLeagues: RequestState.IDLE,
		leaguesForJoin: RequestState.IDLE,
		leagueRankings: RequestState.IDLE,
		leagueUsers: RequestState.IDLE,
		default: RequestState.IDLE,
	},
	error: null,
};

const onRequest = (state: ILeaguesState) => ({
	...state,
	requestState: {
		...state.requestState,
		default: RequestState.Requested,
	},
});

const onLeagueReceived = (state: ILeaguesState, payload: ILeague) => ({
	...state,
	leagues: concat(state.leagues, payload),
	requestState: {
		...state.requestState,
		default: RequestState.Received,
	},
});

const onLeagueUpdated = (state: ILeaguesState, payload: ILeague) => ({
	...state,
	leagues: map(state.leagues, (league) => (league.id === payload.id ? payload : league)),
	requestState: {
		...state.requestState,
		default: RequestState.Received,
	},
});

const onError = (state: ILeaguesState, error: string) => ({
	...state,
	requestState: {
		...state.requestState,
		default: RequestState.Error,
	},
	error,
});

const onSuccess = (state: ILeaguesState) => ({
	...state,
	requestState: {
		...state.requestState,
		default: RequestState.Received,
	},
});

export const leagues = createReducer({}, initial)
	.on(fetchLeagues, (state) => ({
		...state,
		requestState: {
			...state.requestState,
			leagues: RequestState.Requested,
		},
	}))
	.on(fetchLeaguesSuccess, (state, payload) => ({
		...state,
		leagues: payload,
		requestState: {
			...state.requestState,
			leagues: RequestState.Received,
		},
	}))
	.on(fetchLeaguesFailed, (state, error) => ({
		...state,
		requestState: {
			...state.requestState,
			leagues: RequestState.Error,
		},
		error,
	}))
	.on(createLeague, onRequest)
	.on(createLeagueSuccess, onLeagueReceived)
	.on(createLeagueFailed, onError)
	.on(updateLeague, onRequest)
	.on(updateLeagueSuccess, onLeagueUpdated)
	.on(updateLeagueFailed, onError)
	.on(inviteToLeague, onRequest)
	.on(inviteToLeagueSuccess, onSuccess)
	.on(inviteToLeagueFailed, onError)
	.on(setLeagueCreated, (state) => ({
		...state,
		leagueCreated: true,
	}))
	.on(clearLeagueCreated, (state) => ({
		...state,
		leagueCreated: false,
	}))
	.on(fetchLeaguesForJoin, (state) => ({
		...state,
		requestState: {
			...state.requestState,
			leaguesForJoin: RequestState.Requested,
		},
	}))
	.on(fetchLeaguesForJoinSuccess, (state, payload) => ({
		...state,
		leaguesForJoin: {
			leagues: concat(state.leaguesForJoin.leagues, payload.leagues),
			nextPage: payload.nextPage,
		},
		requestState: {
			...state.requestState,
			leaguesForJoin: RequestState.Received,
		},
	}))
	.on(fetchLeaguesForJoinFailed, (state, error) => ({
		...state,
		requestState: {
			...state.requestState,
			leaguesForJoin: RequestState.Error,
		},
		error,
	}))
	.on(clearLeaguesForJoin, (state) => ({
		...state,
		leaguesForJoin: {
			leagues: [],
			nextPage: false,
		},
	}))
	.on(joinLeague, onRequest)
	.on(joinLeagueSuccess, onLeagueReceived)
	.on(joinLeagueFailed, onError)
	.on(setLeagueJoined, (state, payload) => ({
		...state,
		leagueJoined: payload,
	}))
	.on(clearLeagueJoined, (state) => ({
		...state,
		leagueJoined: null,
	}))
	.on(leaveLeague, onRequest)
	.on(leaveLeagueSuccess, (state, payload) => ({
		...state,
		leagues: filter(state.leagues, (league) => league.id !== payload),
		requestState: {
			...state.requestState,
			default: RequestState.Received,
		},
	}))
	.on(leaveLeagueFailed, onError)
	.on(fetchLeagueRankings, (state) => ({
		...state,
		requestState: {
			...state.requestState,
			leagueRankings: RequestState.Requested,
		},
	}))
	.on(fetchLeagueRankingsSuccess, (state, payload) => ({
		...state,
		leagueRankings: {
			rankings: concat(state.leagueRankings.rankings, payload.rankings),
			user: payload.user,
			nextPage: payload.nextPage,
		},
		requestState: {
			...state.requestState,
			leagueRankings: RequestState.Received,
		},
	}))
	.on(fetchLeagueRankingsFailed, (state, error) => ({
		...state,
		requestState: {
			...state.requestState,
			leaguesRankings: RequestState.Error,
		},
		error,
	}))
	.on(clearLeagueRankings, (state) => ({
		...state,
		leagueRankings: {
			rankings: [],
			user: null,
			nextPage: false,
		},
	}))
	.on(fetchLeagueUsers, (state) => ({
		...state,
		requestState: {
			...state.requestState,
			leagueUsers: RequestState.Requested,
		},
	}))
	.on(fetchLeagueUsersSuccess, (state, payload) => ({
		...state,
		leagueUsers: {
			users: concat(state.leagueUsers.users, payload.users),
			nextPage: payload.nextPage,
		},
		requestState: {
			...state.requestState,
			leagueUsers: RequestState.Received,
		},
	}))
	.on(fetchLeagueUsersFailed, (state, error) => ({
		...state,
		requestState: {
			...state.requestState,
			leagueUsers: RequestState.Error,
		},
		error,
	}))
	.on(clearLeagueUsers, (state) => ({
		...state,
		leagueUsers: {
			users: [],
			nextPage: false,
		},
	}))
	.on(removeUser, onRequest)
	.on(removeUserSuccess, (state, payload) => ({
		...state,
		leagueUsers: {
			...state.leagueUsers,
			users: filter(state.leagueUsers.users, (user) => user.userId !== payload),
		},
		requestState: {
			...state.requestState,
			default: RequestState.Received,
		},
	}))
	.on(removeUserFailed, onError)
	.on(openInvitesSentModal, (state) => ({
		...state,
		isInvitesSentModalOpen: true,
	}))
	.on(closeInvitesSentModal, (state) => ({
		...state,
		isInvitesSentModalOpen: false,
	}))
	.on(openRemoveUserModal, (state, payload) => ({
		...state,
		removeUserModal: payload,
	}))
	.on(closeRemoveUserModal, (state) => ({
		...state,
		removeUserModal: null,
	}))
	.on(openLeaveLeagueModal, (state, payload) => ({
		...state,
		leaveLeagueModal: payload,
	}))
	.on(closeLeaveLeagueModal, (state) => ({
		...state,
		leaveLeagueModal: null,
	}))
	.on(setContestsForLeagues, (state, payload) => ({
		...state,
		contestsForLeagues: payload,
	}))
	.on(fetchLeagueForJoin, onRequest)
	.on(fetchLeagueForJoinSuccess, (state, payload) => ({
		...state,
		leagueForJoin: payload,
		requestState: {
			...state.requestState,
			default: RequestState.Received,
		},
	}))
	.on(fetchLeagueForJoinFailed, onError)
	.on(clearLeagueForJoin, (state) => ({
		...state,
		leagueForJoin: undefined,
	}))
	.on(setRankingFilters, (state, filters) => ({
		...state,
		leagueRankingFilters: filters,
	}))
	.on(fetchGotcLeague, (state) => ({
		...state,
		requestState: {
			...state.requestState,
			gotcLeagues: RequestState.Requested,
		},
	}))
	.on(fetchGotcLeagueSuccess, (state, leagues) => ({
		...state,
		gotcLeague: leagues.length ? leagues[0] : null,
		requestState: {
			...state.requestState,
			gotcLeagues: RequestState.Received,
		},
	}));
